import { useCallback, useLayoutEffect, useMemo, useState, useSyncExternalStore } from 'react';
import type { IMenuOpenState } from './IMenuOpenState';
import { browserStorage, readMenuOpen, writeMenuOpen } from './menuOpenStore';
import { menuSession } from './menuSessionStore';

const NONE = (): string | undefined => undefined;

/**
 * Пам'ять розгорнутих розділів лівого меню окремо для кожного модуля: збережене в localStorage (menuOpenStore) поверх змін цього сеансу,
 * тож перемикання працює й без сховища. Зміна модуля в тій самій оболонці одразу читає стан нового модуля. Розділи, для яких `slotOf` дає
 * слот (у меню модуля — menuSessionSlot: «Налаштування» на шаблон меню, «Проєктування» й розділи типів на модуль), не зберігаються: вони
 * живуть у пам'яті вкладки (menuSession) — переживають вихід із модуля й «Назад» браузера, а нове відкриття модуля (`visit` — ключ
 * переходу на корінь модуля, як openModule прототипу) чи перезавантаження повертають типовий стан.
 */
export function useMenuOpenState(moduleKey: string, slotOf: (id: string) => string | undefined = NONE, visit?: string): IMenuOpenState {
  const stored = useMemo(() => readMenuOpen(browserStorage(), moduleKey), [moduleKey]);
  const [changed, setChanged] = useState<Record<string, Record<string, boolean>>>({});
  const session = useSyncExternalStore(menuSession.subscribe, menuSession.snapshot, menuSession.snapshot);
  // Нове відкриття: слоти вже типові в цьому рендері, очищення сховища — до показу (layout-ефект), один раз на перехід історії.
  const fresh = visit !== undefined && !menuSession.isOpened(visit);
  useLayoutEffect(() => { if (visit !== undefined) menuSession.open(visit); }, [visit]);
  const current = useMemo(() => ({ ...stored, ...changed[moduleKey] }), [stored, changed, moduleKey]);
  const slotValue = useCallback((slot: string) => (fresh ? undefined : session[slot]), [fresh, session]);
  const isOpen = useCallback((id: string, fallback: boolean) => {
    const slot = slotOf(id);
    return (slot ? slotValue(slot) : current[id]) ?? fallback;
  }, [current, slotOf, slotValue]);
  const toggle = useCallback((id: string, fallback: boolean) => {
    const slot = slotOf(id);
    if (slot) {
      if (visit !== undefined) menuSession.open(visit);
      menuSession.set(slot, !(slotValue(slot) ?? fallback));
      return;
    }
    const value = !(current[id] ?? fallback);
    const kept = Object.entries({ ...current, [id]: value }).filter(([k]) => !slotOf(k));
    writeMenuOpen(browserStorage(), moduleKey, Object.fromEntries(kept));
    setChanged((prev) => ({ ...prev, [moduleKey]: { ...prev[moduleKey], [id]: value } }));
  }, [current, moduleKey, slotOf, slotValue, visit]);
  return useMemo(() => ({ isOpen, toggle }), [isOpen, toggle]);
}
